import { useState } from 'react'
import { lumi } from '@/lib/lumi'

export interface Course {
  _id: string
  title: string
  description?: string
  skill: string
  skillLabel?: string
  type: 'free' | 'premium'
  status: 'active' | 'inactive'
  price?: number
  coverUrl?: string
  htmlContent?: string
  relatedPremiumId?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

export interface CoursePurchase {
  _id: string
  courseId: string
  courseTitle?: string
  collaboratorUserId: string
  collaboratorName?: string
  collaboratorEmail?: string
  method: 'stripe' | 'transfer'
  amount: number
  status: 'pending' | 'paid' | 'rejected'
  stripeSessionId?: string
  transferReference?: string
  validatedAt?: string
  validatedBy?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

export interface CourseProgress {
  _id: string
  courseId: string
  courseTitle?: string
  courseSkill?: string
  courseSkillLabel?: string
  courseType?: string
  collaboratorUserId: string
  status: 'in_progress' | 'completed'
  completedAt?: string
  badgeUrl?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

export function useCourses() {
  const [courses, setCourses] = useState<Course[]>([])
  const [purchases, setPurchases] = useState<CoursePurchase[]>([])
  const [progress, setProgress] = useState<CourseProgress[]>([])
  const [loading, setLoading] = useState(false)

  // ── Cursos ──────────────────────────────────────────────
  const fetchAllCourses = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.courses.list({
        sort: { createdAt: -1 },
      })
      setCourses(list as Course[])
    } catch (e) {
      console.error('Error al cargar cursos:', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchActiveCourses = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.courses.list({
        filter: { status: 'active' },
        sort: { createdAt: -1 },
      })
      setCourses(list as Course[])
    } catch (e) {
      console.error('Error al cargar cursos activos:', e)
    } finally {
      setLoading(false)
    }
  }

  const createCourse = async (data: Partial<Course>) => {
    const now = new Date().toISOString()
    return lumi.entities.courses.create({
      ...data,
      price: data.price ? Number(data.price) : 0,
      createdAt: now,
      updatedAt: now,
    })
  }

  const updateCourse = async (id: string, data: Partial<Course>) => {
    const now = new Date().toISOString()
    return lumi.entities.courses.update(id, {
      ...data,
      price: data.price !== undefined ? Number(data.price) : undefined,
      updatedAt: now,
    })
  }

  const deleteCourse = async (id: string) => {
    return lumi.entities.courses.delete(id)
  }

  // ── Compras ─────────────────────────────────────────────
  const fetchPurchases = async (filter?: Record<string, unknown>) => {
    try {
      const { list } = await lumi.entities.course_purchases.list({
        filter: filter ?? {},
        sort: { createdAt: -1 },
      })
      setPurchases(list as CoursePurchase[])
    } catch (e) {
      console.error('Error al cargar compras:', e)
    }
  }

  const fetchPurchasesByCollaborator = async (collaboratorUserId: string) => {
    try {
      const { list } = await lumi.entities.course_purchases.list({
        filter: { collaboratorUserId, status: 'paid' },
        sort: { createdAt: -1 },
      })
      setPurchases(list as CoursePurchase[])
    } catch (e) {
      console.error('Error al cargar compras del colaborador:', e)
    }
  }

  const createPurchase = async (data: Partial<CoursePurchase>) => {
    const now = new Date().toISOString()
    return lumi.entities.course_purchases.create({
      ...data,
      amount: Number(data.amount ?? 0),
      createdAt: now,
      updatedAt: now,
    })
  }

  const updatePurchase = async (id: string, data: Partial<CoursePurchase>) => {
    const now = new Date().toISOString()
    return lumi.entities.course_purchases.update(id, { ...data, updatedAt: now })
  }

  // ── Progreso ─────────────────────────────────────────────
  const fetchProgressByCollaborator = async (collaboratorUserId: string) => {
    try {
      const { list } = await lumi.entities.course_progress.list({
        filter: { collaboratorUserId },
        sort: { createdAt: -1 },
      })
      setProgress(list as CourseProgress[])
    } catch (e) {
      console.error('Error al cargar progreso:', e)
    }
  }

  const fetchAllProgress = async () => {
    try {
      const { list } = await lumi.entities.course_progress.list({
        sort: { createdAt: -1 },
      })
      setProgress(list as CourseProgress[])
    } catch (e) {
      console.error('Error al cargar todo el progreso:', e)
    }
  }

  const startCourse = async (
    courseId: string,
    collaboratorUserId: string,
    courseMeta: { title?: string; skill?: string; skillLabel?: string; type?: string }
  ) => {
    const now = new Date().toISOString()
    return lumi.entities.course_progress.create({
      courseId,
      collaboratorUserId,
      status: 'in_progress',
      courseTitle: courseMeta.title ?? '',
      courseSkill: courseMeta.skill ?? '',
      courseSkillLabel: courseMeta.skillLabel ?? '',
      courseType: courseMeta.type ?? 'free',
      creator: collaboratorUserId,
      createdAt: now,
      updatedAt: now,
    })
  }

  const completeCourse = async (progressId: string, courseId: string) => {
    const now = new Date().toISOString()
    // Generar insignia genérica con iniciales del curso
    const badgeUrl = `https://static.lumi.new/93/93deb8f4c6e571fa108d99af77ea2979.webp`
    return lumi.entities.course_progress.update(progressId, {
      status: 'completed',
      completedAt: now,
      badgeUrl,
      updatedAt: now,
    })
  }

  return {
    courses,
    purchases,
    progress,
    loading,
    fetchAllCourses,
    fetchActiveCourses,
    createCourse,
    updateCourse,
    deleteCourse,
    fetchPurchases,
    fetchPurchasesByCollaborator,
    createPurchase,
    updatePurchase,
    fetchProgressByCollaborator,
    fetchAllProgress,
    startCourse,
    completeCourse,
  }
}
